import { Component, Input } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { RtRouteService } from '@bds/data-access';
import { faSave } from '@bds/fa-svg-icons';
import { ScrollSpyService } from '@bds/helpers';
import { RtRouteCode } from '@bds/railtrac-models';
import { faGlobeAmericas } from '@fortawesome/pro-light-svg-icons';
import { faArrowsH } from '@fortawesome/pro-regular-svg-icons';
import { faPen, faRoute } from '@fortawesome/pro-solid-svg-icons';
import { firstValueFrom, of } from 'rxjs';
import { RouteCodeFormService } from './bds-route-code/bds-route-code-form.service';
import { faBars, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { MatSnackBar } from '@angular/material/snack-bar';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'bds-route',
    templateUrl: './bds-route.component.html',
    styleUrls: ['./bds-route.component.css'],
    providers: [RouteCodeFormService],
})
export class BdsRouteComponent {
    public detailViewPath = 'routes/detail';
    selectedrouteCode: string;
    $selectedRoute = of<RtRouteCode | null>(null);
    duoIconRoute = faRoute;
    iconRouteDetail = faGlobeAmericas;
    iconRouteSegments = faArrowsH;
    iconSave = faSave;
    iconEdit = faPen;
    @Input() showMenu = true;
    @Input() readOnly = false;

    errors: [
        { validationField: string; validationMessage: string; validationMessageTypeId: number },
    ];
    iconMenu = faBars;
    iconError = faExclamationTriangle;
    showFiller = false;

    private updatedSegments: any[] = [];
    private currentRoute: Partial<RtRouteCode> = {};
    private originalSegments: any[] = [];
    private changedSegments = new Map<string | number, any>();

    constructor(
        private routeService: RtRouteService,
        public scrollSpyService: ScrollSpyService,
        public router: Router,
        public route: ActivatedRoute,
        public routeCodeFormService: RouteCodeFormService,
        private snackBar: MatSnackBar,
        private location: Location,
    ) {}

    @Input() set routeCode(value: string) {
        this.selectedrouteCode = value;
        this.$selectedRoute = this.routeService.read(value).pipe(
            tap((route) => {
                if (!route) return;
                this.currentRoute = { ...route };
                this.updatedSegments = (route as any).segments ? [...(route as any).segments] : [];
                this.originalSegments = ((route as any).segments ?? []).map((s: any) => ({ ...s }));
                this.updatedSegments = [...this.originalSegments];
                this.changedSegments.clear();
            }),
        );
    }

    private getSegmentKey(seg: any): string | number | undefined {
        return seg?.id ?? seg?.segmentNo ?? seg?.segmentId;
    }

    onSegmentUpdated(updatedSegment: any): void {
        const key = this.getSegmentKey(updatedSegment);
        if (key == null) return;

        const base = this.originalSegments.find((s) => this.getSegmentKey(s) === key) ?? {};
        const prev = this.changedSegments.get(key) ?? {};
        const merged = { ...base, ...prev, ...updatedSegment };
        this.changedSegments.set(key, merged);
    }

    async onEditClick(): Promise<void> {
        if (this.readOnly) {
            void this.router.navigate([this.detailViewPath, this.selectedrouteCode], {
                queryParams: { isReturn: btoa('true') },
            });
            return;
        }

        const originalKeys = new Set(this.originalSegments.map((s) => this.getSegmentKey(s)!));
        const mergedSegments = this.originalSegments.map((s) => {
            const key = this.getSegmentKey(s)!;
            return this.changedSegments.get(key) ?? s;
        });
        for (const [key, seg] of this.changedSegments) {
            if (!originalKeys.has(key)) mergedSegments.push(seg);
        }

        const formData = this.routeCodeFormService.form.getRawValue();
        const routeDataToSave: any = {
            ...formData,
            segments: mergedSegments,
        };

        try {
            const result = await firstValueFrom(this.routeService.update(routeDataToSave));

            if (result) {
                if (result['relatedObject']) {
                    this.snackBar.open('Route changes saved!', 'Ok', { duration: 3000 });
                    this.routeService.refreshRouteGridSource.next(routeDataToSave);
                    this.navigateToReturnUrl();
                    return;
                }
                this.snackBar.open('Route changes failed!', 'Ok', { duration: 3000 });
            }
        } catch (error) {
            this.snackBar.open('Error saving route changes!', 'Ok', { duration: 5000 });
        }
    }

    private navigateToReturnUrl() {
        const isReturn = this.route.snapshot.queryParams['isReturn'];

        if (atob(isReturn ?? '') === 'true') {
            this.location.back();
        }
    }
}
